import type React from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { captureException } from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { PauseIcon, PlayIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useVoicePlayback } from '@/model/contexts/VoicePlaybackContext'
import { firebaseStorageUrlQuery } from '@/state/firebase-storage/firebaseStorage'

export const formatVoiceClipDuration = (milliseconds: number) => {
  const seconds = Math.floor(milliseconds / 1000)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

export interface VoiceClipPlayerProps {
  readonly storagePath?: string
  readonly src?: string
  readonly durationMs?: number
  readonly isRecording?: boolean
  readonly queryScope?: string
  readonly className?: string
}

export const VoiceClipPlayer: React.FC<VoiceClipPlayerProps> = ({
  storagePath,
  src,
  durationMs,
  isRecording,
  queryScope,
  className,
}) => {
  const intl = useIntl()
  const id = useId()
  const playback = useVoicePlayback()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [positionMs, setPositionMs] = useState(0)
  const [loadedDurationMs, setLoadedDurationMs] = useState<number>()
  const [playRequested, setPlayRequested] = useState(false)
  const { data: storageUrl, refetch } = useQuery(firebaseStorageUrlQuery(src ? undefined : storagePath, queryScope))
  const url = src ?? storageUrl ?? undefined

  const totalMs = durationMs ?? loadedDurationMs ?? 0
  const active = playing || positionMs > 0
  const progress = totalMs > 0 ? Math.min(positionMs / totalMs, 1) : 0
  const totalLabel = formatVoiceClipDuration(totalMs)

  const reset = () => {
    const audio = audioRef.current
    audio?.pause()
    if (audio) audio.currentTime = 0
    setPlaying(false)
    setPositionMs(0)
    setPlayRequested(false)
  }

  // A moderator's deletion unmounts the player of the live message: the clip must not play on from a detached element.
  useEffect(() => {
    const audio = audioRef.current
    return () => {
      audio?.pause()
      playback.release(id)
    }
  }, [playback, id])

  const startPlayback = async () => {
    const audio = audioRef.current
    if (!audio) return
    playback.claim(id, reset)
    try {
      await audio.play()
      setPlaying(true)
    } catch (error) {
      captureException(error, { tags: { storagePath } })
      reset()
    }
  }

  const toggle = async () => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }
    // The storage rules refuse a clip until its message reaches the server, and the thread shows the sender's message
    // before that: a URL refused in that window is asked for again on play, and the clip starts once it has loaded.
    // Claiming the player at the click lets another clip or a recording started meanwhile cancel the pending start.
    if (!url) {
      if (storagePath && !src) {
        playback.claim(id, reset)
        setPlayRequested(true)
        const { data } = await refetch()
        if (!data) setPlayRequested(false)
      }
      return
    }
    await startPlayback()
  }

  return (
    <div
      className={cn(
        'flex w-[360px] max-w-full items-center gap-3 rounded-lg bg-gray-100 px-3 py-2 text-base',
        className
      )}>
      <button
        type="button"
        aria-label={
          playing
            ? intl.formatMessage({ id: 'message.voice.pause' })
            : intl.formatMessage(
                { id: isRecording ? 'message.voice.playRecording' : 'message.voice.play' },
                { duration: totalLabel }
              )
        }
        onClick={toggle}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-blue-700 text-white hover:bg-blue-600">
        {playing ? <PauseIcon className="size-3.5 fill-white" /> : <PlayIcon className="size-3.5 fill-white" />}
      </button>
      <div aria-hidden="true" className="relative h-1 grow rounded-sm bg-gray-300">
        <div className="absolute inset-y-0 left-0 rounded-sm bg-blue-700" style={{ width: `${progress * 100}%` }} />
        {active && (
          <div
            className="absolute -top-1 -ml-1.5 size-3 rounded-full bg-blue-700"
            style={{ left: `${progress * 100}%` }}
          />
        )}
      </div>
      <span className="text-xs text-gray-500 tabular-nums">
        {active ? `${formatVoiceClipDuration(positionMs)} / ${totalLabel}` : totalLabel}
      </span>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(event) => {
          const { duration } = event.currentTarget
          if (Number.isFinite(duration) && duration > 0) setLoadedDurationMs(duration * 1000)
          if (playRequested) {
            setPlayRequested(false)
            startPlayback().catch(() => null)
          }
        }}
        onTimeUpdate={(event) => {
          setPositionMs(event.currentTarget.currentTime * 1000)
        }}
        onEnded={() => {
          playback.release(id)
          reset()
        }}
        onError={() => {
          if (url) captureException(new Error(`Voice clip failed to load: ${storagePath ?? 'local recording'}`))
          reset()
        }}
      />
    </div>
  )
}
